import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import { Modal } from "react-bootstrap";
import SchedulerGrid from "../EmployeeView/SchedulerGrid";
import ResourceTaskCard from "./ResourceTaskCard";
import HodAllocateModal from "../HodView/HodAllocateModal";
import {
  fetchAssignments,
  deleteAssignment,
  fetchCalendar,
} from "../../../services/assignmentApi";
import { fetchClients, getClientImageSrc } from "../../../services/clientApi";

const CARD_COLORS = [
  { bg: "#fff8e1", border: "#ffa726", tagBg: "#fff3cd", tagColor: "#856404" },
  { bg: "#e8f5e9", border: "#66bb6a", tagBg: "#d1e7dd", tagColor: "#0a3622" },
  { bg: "#e3f2fd", border: "#42a5f5", tagBg: "#cfe2ff", tagColor: "#084298" },
  { bg: "#fce4ec", border: "#ef5350", tagBg: "#f8d7da", tagColor: "#842029" },
  { bg: "#f3e5f5", border: "#ab47bc", tagBg: "#e9d8fd", tagColor: "#4a0e5c" },
];

function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  return d;
}

function fmt(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseLocalDate(str) {
  if (!str) return new Date(NaN);
  const [y, mo, d] = str.slice(0, 10).split("-").map(Number);
  return new Date(y, mo - 1, d);
}

function normalizeId(v) {
  return v && typeof v === "object" ? v._id : v;
}

function getWeekDays(weekStart) {
  const names = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  return names.map((full, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return {
      full,
      short: full.slice(0, 3),
      date: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    };
  });
}

function toResourceTask(assignment, empName, empId, designation, weekStartDate, colorIndex) {
  const weekStartIso = isoDate(weekStartDate);
  const startIso = isoDate(parseLocalDate(assignment.startDate));
  const endIso   = isoDate(parseLocalDate(assignment.endDate));

  const from = Math.max(0, Math.round((parseLocalDate(startIso) - parseLocalDate(weekStartIso)) / 86400000));
  const to   = Math.min(4, Math.round((parseLocalDate(endIso)   - parseLocalDate(weekStartIso)) / 86400000));
  if (to < from) return null;

  return {
    id:             String(assignment.assignmentId) + colorIndex,
    assignmentId:   assignment.assignmentId,
    category:       assignment.clientName      || "General",
    title:          assignment.projectName     || assignment.taskDescription || "Task",
    client:         assignment.clientName      || "—",
    project:        assignment.projectName     || "—",
    task:           assignment.taskDescription || "—",
    notes:          assignment.notes           || "—",
    assignedTo:     empName,
    assignedBy:     assignment.assignedBy      || "—",
    designation:    designation                || "—",
    startDate:      assignment.startDate       || null,
    endDate:        assignment.endDate         || null,
    startDay:       from,
    endDay:         to,
    colorIndex:     colorIndex % CARD_COLORS.length,
    clientId:       normalizeId(assignment.clientId),
    projectId:      normalizeId(assignment.projectId),
    departmentId:   normalizeId(assignment.departmentId),
    employeeId:     String(empId),
  };
}

const SELECT_STYLE = {
  paddingLeft: 32, paddingRight: 28, paddingTop: 7, paddingBottom: 7,
  borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12,
  color: "#334155", background: "#fff", cursor: "pointer", outline: "none",
  fontWeight: 500, appearance: "none", WebkitAppearance: "none",
};

const ResourceView = () => {
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));

  // Filters
  const [filterProjectId,  setFilterProjectId]  = useState("");
  const [filterEmployeeId, setFilterEmployeeId] = useState("");

  // Dropdown options from API
  const [clients, setClients] = useState([]);

  // Assignment data
  const [assignments,  setAssignments]  = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  // Allocate modal
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editSchedule, setEditSchedule] = useState(null);

  // Detail popup
  const [detailCard, setDetailCard] = useState(null);

  // Load static dropdown data once
  useEffect(() => {
    fetchClients().then(setClients).catch(() => {});
  }, []);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 4);
    return d;
  }, [weekStart]);

  const days = useMemo(() => getWeekDays(weekStart), [weekStart]);

  // Load assignments + calendar whenever the week changes
  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);

    Promise.all([
      fetchAssignments(),
      fetchCalendar(isoDate(weekStart), isoDate(weekEnd)),
    ])
      .then(([asgns, cal]) => {
        setAssignments(asgns);
        setCalendarData(cal);
      })
      .catch((err) => setError(err.message || "Failed to load assignments."))
      .finally(() => setLoading(false));
  }, [weekStart, weekEnd]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePrev = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const handleNext = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };

  const openEditModal = (assignment) => { setEditSchedule(assignment); setModalOpen(true); };
  const closeModal    = () => { setModalOpen(false); setEditSchedule(null); };
  const handleSaved   = () => { closeModal(); loadData(); };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assignment?")) return;
    try {
      await deleteAssignment(id);
      loadData();
    } catch (err) {
      alert(err.message || "Failed to delete assignment.");
    }
  };

  // Collect unique employees from calendarData for the filter dropdown
  const allEmployees = useMemo(() => {
    const seen = new Map();
    for (const emp of calendarData) {
      if (emp.employeeId && !seen.has(emp.employeeId))
        seen.set(emp.employeeId, emp.employeeName || emp.employeeId);
    }
    return Array.from(seen.entries()).map(([_id, name]) => ({ _id, name }));
  }, [calendarData]);

  // Transform calendarData → schedulerTasks (same format as EmployeeView)
  const allSchedulerTasks = useMemo(() => {
    let colorIndex = 0;
    const tasks = [];
    calendarData.forEach((emp) => {
      (emp.assignments || []).forEach((a) => {
        const task = toResourceTask(a, emp.employeeName, emp.employeeId, emp.designation, weekStart, colorIndex++);
        if (task) tasks.push(task);
      });
    });
    return tasks;
  }, [calendarData, weekStart]);

  // Collect unique projects from calendar data for the filter dropdown
  const allProjects = useMemo(() => {
    const seen = new Map();
    for (const t of allSchedulerTasks) {
      if (t.projectId && !seen.has(t.projectId))
        seen.set(t.projectId, t.project || t.projectId);
    }
    return Array.from(seen.entries()).map(([_id, name]) => ({ _id, name }));
  }, [allSchedulerTasks]);

  // Apply project/employee filters to scheduler tasks
  const schedulerTasks = useMemo(() => allSchedulerTasks.filter((t) => {
    if (filterProjectId  && t.projectId  !== filterProjectId)  return false;
    if (filterEmployeeId && t.employeeId !== filterEmployeeId) return false;
    return true;
  }), [allSchedulerTasks, filterProjectId, filterEmployeeId]);

  const displayCategories = useMemo(() =>
    [...new Set(schedulerTasks.map((t) => t.category))],
    [schedulerTasks]
  );

  // Map clientName → image URL for the scheduler grid left column
  const categoryImages = useMemo(() => {
    const map = {};
    for (const client of clients) {
      if (client.clientImage) {
        map[client.clientName] = getClientImageSrc(client.clientImage);
      }
    }
    return map;
  }, [clients]);

  return (
    <Fragment>
      <div>
        {/* Controls bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, background: "#fff", borderRadius: 12, padding: "14px 20px", marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>

          {/* Row 1: date range + ADD JOB */}
          <div className="d-flex align-items-center justify-content-between mb-3 col-12" style={{ gap: 12, flexWrap: "wrap" }}>
            <div className="d-flex align-items-center gap-2">
              <button onClick={handlePrev}
                style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 11 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <i className="fa fa-chevron-left" />
              </button>
              <i className="fa fa-calendar" style={{ color: "#94a3b8", fontSize: 13 }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: "#334155", whiteSpace: "nowrap" }}>
                {fmt(weekStart)} – {fmt(weekEnd)}
              </span>
              <button onClick={handleNext}
                style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 11 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <i className="fa fa-chevron-right" />
              </button>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { setEditSchedule(null); setModalOpen(true); }}
              style={{ borderRadius: 4, fontWeight: 700, whiteSpace: "nowrap", padding: "8px 18px" }}
            >
              <i className="fa fa-plus me-1" style={{ fontSize: 11 }} /> ADD JOB
            </button>
          </div>

          {/* Row 2: filters (Project + Resource) */}
          <div className="d-flex align-items-center justify-content-end gap-2 col-12 flex-wrap" style={{ overflowX: "auto" }}>
            {/* Project filter */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <i className="fa fa-folder" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
              <select value={filterProjectId} onChange={(e) => setFilterProjectId(e.target.value)} style={SELECT_STYLE}>
                <option value="">Select Project</option>
                {allProjects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
              <i className="fa fa-angle-down" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
            </div>

            {/* Resource filter */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <i className="fa fa-user" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
              <select value={filterEmployeeId} onChange={(e) => setFilterEmployeeId(e.target.value)} style={SELECT_STYLE}>
                <option value="">Select Resource</option>
                {allEmployees.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
              </select>
              <i className="fa fa-angle-down" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
            </div>

            {/* Clear filters */}
            {(filterProjectId || filterEmployeeId) && (
              <button
                onClick={() => { setFilterProjectId(""); setFilterEmployeeId(""); }}
                style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, cursor: "pointer", fontWeight: 500 }}
              >
                <i className="fa fa-times me-1" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between">
            <span><i className="fa fa-exclamation-circle me-2" />{error}</span>
            <button className="btn btn-sm btn-outline-danger" onClick={loadData}>
              <i className="fa fa-refresh me-1" /> Retry
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <p className="mt-2 text-muted">Loading assignments…</p>
          </div>
        ) : (
          <>
            {/* Scheduler grid */}
            <div style={{ marginBottom: 28 }}>
              <SchedulerGrid
                days={days}
                categories={displayCategories}
                tasks={schedulerTasks}
                cardColors={CARD_COLORS}
                onTaskClick={(task) => setDetailCard(task)}
                CardComponent={ResourceTaskCard}
                categoryImages={categoryImages}
              />
            </div>

          </>
        )}

        {/* Allocate Modal */}
        <HodAllocateModal
          isOpen={modalOpen}
          onClose={closeModal}
          editData={editSchedule}
          onSaved={handleSaved}
        />

        {/* Card Detail Modal */}
        <Modal show={!!detailCard} onHide={() => setDetailCard(null)} centered>
          <Modal.Header closeButton className="border-bottom pb-3">
            <Modal.Title style={{ fontSize: 16 }}>
              <i className="fa fa-calendar-check-o me-2 text-primary" />
              Task Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailCard && (() => {
              const color = CARD_COLORS[detailCard.colorIndex] || CARD_COLORS[0];
              const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
              return (
                <div style={{
                  borderLeft: `4px solid ${color.border}`,
                  background: color.bg,
                  borderRadius: "0 10px 10px 0",
                  padding: 18,
                }}>
                  <h5 style={{ fontWeight: 700, marginBottom: 14, color: color.border }}>
                    {detailCard.client}
                  </h5>
                  {[
                    ["Project",    detailCard.project],
                    ["Assign By",  detailCard.assignedBy],
                    ["Project Desc", detailCard.task],
                    ["Task",        detailCard.notes],
                    ["Start Date", fmtDate(detailCard.startDate)],
                    ["End Date",   fmtDate(detailCard.endDate)],
                  ].map(([label, value]) => (
                    <div key={label} className="mb-2" style={{ fontSize: 13, display: "flex", gap: 6 }}>
                      <strong style={{ minWidth: 80, flexShrink: 0 }}>{label}:</strong>
                      <span style={{ color: "#495057" }}>{value || "—"}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => {
                const id = detailCard?.assignmentId;
                setDetailCard(null);
                handleDelete(id);
              }}
            >
              <i className="fa fa-trash me-1" /> Delete
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                const fullAssignment = assignments.find(
                  (a) => String(a._id) === String(detailCard?.assignmentId)
                ) || null;
                setDetailCard(null);
                openEditModal(fullAssignment);
              }}
            >
              <i className="fa fa-pencil me-1" /> Edit
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </Fragment>
  );
};

export default ResourceView;
